import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  EnrollmentStatusAuditService,
  EnrollmentAuditBulkFixBatchDetail,
  EnrollmentAuditBulkFixItem
} from '../../services/enrollment-status-audit.service';
import { ToastService } from '../../services/toast.service';
import { AuthService } from '../../services/auth.service';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-enrollment-audit-bulk-fix',
  templateUrl: './enrollment-audit-bulk-fix.component.html',
  styleUrls: ['./enrollment-audit-bulk-fix.component.scss']
})
export class EnrollmentAuditBulkFixComponent implements OnInit, OnDestroy {
  private readonly ALLOWED_EMAIL = 'jdanigo@cloudsoft.site';

  batchId = '';
  batch: EnrollmentAuditBulkFixBatchDetail | null = null;
  loading = false;
  cancelling = false;
  private pollingHandle?: ReturnType<typeof setInterval>;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private auditService: EnrollmentStatusAuditService,
    private toast: ToastService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    const email = (this.authService.getCurrentUser()?.email ?? '').toLowerCase();
    if (email !== this.ALLOWED_EMAIL.toLowerCase()) {
      this.router.navigate(['/dashboard']);
      return;
    }
    this.batchId = this.route.snapshot.paramMap.get('batchId') ?? '';
    if (!this.batchId) {
      this.toast.showToast('error', 'BatchId inválido.');
      this.router.navigate(['/dashboard/admin/enrollment-audit']);
      return;
    }
    this.refresh();
    this.startPolling();
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  async refresh(): Promise<void> {
    this.loading = true;
    try {
      this.batch = await firstValueFrom(this.auditService.getBulkFixBatch(this.batchId));
      if (this.batch.status !== 'Running' && this.batch.status !== 'Pending') {
        this.stopPolling();
      }
    } catch {
      this.toast.showToast('error', 'Error cargando el batch.');
    } finally {
      this.loading = false;
    }
  }

  private startPolling(): void {
    if (this.pollingHandle) return;
    this.pollingHandle = setInterval(() => {
      void this.refresh();
    }, 2000);
  }

  private stopPolling(): void {
    if (this.pollingHandle) {
      clearInterval(this.pollingHandle);
      this.pollingHandle = undefined;
    }
  }

  async cancel(): Promise<void> {
    if (!this.batch || this.batch.status !== 'Running' || this.cancelling) return;
    if (!confirm('¿Cancelar este bulk fix? Los items pendientes quedarán como Skipped.')) return;
    this.cancelling = true;
    try {
      await firstValueFrom(this.auditService.cancelBulkFix(this.batchId));
      this.toast.showToast('info', 'Cancelación solicitada.');
      this.refresh();
    } catch (err: any) {
      const msg = err?.error?.error ?? err?.error?.message ?? err?.error ?? 'Error cancelando.';
      this.toast.showToast('error', typeof msg === 'string' ? msg : 'Error cancelando.');
    } finally {
      this.cancelling = false;
    }
  }

  goBack(): void {
    this.router.navigate(['/dashboard/admin/enrollment-audit']);
  }

  get progress(): number {
    if (!this.batch || this.batch.totalItems === 0) return 0;
    const done = this.batch.successCount + this.batch.failedCount + this.batch.skippedCount;
    return Math.round((done / this.batch.totalItems) * 100);
  }

  get isRunning(): boolean {
    return this.batch?.status === 'Running' || this.batch?.status === 'Pending';
  }

  statusBadgeClass(status: string): string {
    switch (status) {
      case 'Completed': return 'bg-green-100 text-green-800 border-green-300';
      case 'Running': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'Failed': return 'bg-red-100 text-red-800 border-red-300';
      case 'Cancelled': return 'bg-gray-100 text-gray-700 border-gray-300';
      case 'Pending': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'Skipped': return 'bg-gray-100 text-gray-600 border-gray-300';
      default: return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  }

  statusLabel(status: string): string {
    const map: Record<string, string> = {
      'Pending': 'Pendiente',
      'Running': 'En curso',
      'Completed': 'Completado',
      'Failed': 'Falló',
      'Cancelled': 'Cancelado',
      'Skipped': 'Omitido'
    };
    return map[status] ?? status;
  }

  enrollmentStatusLabel(value: string): string {
    const idx = parseInt(value, 10);
    const map: Record<number, string> = {
      0: 'Pendiente', 1: 'Activo', 2: 'Retirado', 3: 'Diferido',
      4: 'Completado', 5: 'Refinanciado', 6: 'Anulado', 7: 'Reembolso',
      8: 'Cesión de Cupo'
    };
    if (!isNaN(idx) && map[idx]) return map[idx];
    return value;
  }

  describeAction(): string {
    if (!this.batch) return '';
    if (this.batch.action === 'SetAutoGenerate') {
      return this.batch.targetValue === 'true' ? 'Activar cobro automático' : 'Desactivar cobro automático';
    }
    return `Cambiar estado → ${this.enrollmentStatusLabel(this.batch.targetValue)}`;
  }

  goToTarget(item: EnrollmentAuditBulkFixItem): void {
    const id = item.targetEnrollmentId ?? item.targetCorporateEnrollmentId;
    if (!id) return;
    const segments = item.targetEnrollmentId != null
      ? ['/dashboard/enrollments/view-v2', id]
      : ['/dashboard/enrollments/corporate/view-v2', id];
    const url = this.router.serializeUrl(this.router.createUrlTree(segments));
    window.open(url, '_blank');
  }

  trackById = (_: number, item: EnrollmentAuditBulkFixItem) => item.id;
}
